import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, MessageCircle, Calendar, Bell, DollarSign, FileText, Activity, FileCheck, Heart, TrendingUp, HandCoins, ShoppingCart, Gift } from 'lucide-react';

// Imágenes del carrusel "VISTA PREVIA" para Monitoreo de la carga de entrenamiento
import m1 from '../assets/img/monitoreo/m1.png';
import m2 from '../assets/img/monitoreo/m2.png';
import m3 from '../assets/img/monitoreo/m3.png';
import m4 from '../assets/img/monitoreo/m4.png';
import m5 from '../assets/img/monitoreo/m5.png';
import m6 from '../assets/img/monitoreo/m6.png';
import m7 from '../assets/img/monitoreo/m7.png';
import plan1 from '../assets/img/planificacion/plan1.png';
import plan2 from '../assets/img/planificacion/plan2.png';
import plan3 from '../assets/img/planificacion/plan3.png';
import plan4 from '../assets/img/planificacion/plan4.png';
import plan5 from '../assets/img/planificacion/plan5.png';
import prev1 from '../assets/img/prevencion/prev1.png';
import prev2 from '../assets/img/prevencion/prev2.png';
import prev3 from '../assets/img/prevencion/prev3.png';
import prev4 from '../assets/img/prevencion/prev4.png';
import prev5 from '../assets/img/prevencion/prev5.png';
import prev6 from '../assets/img/prevencion/prev6.png';
import prev7 from '../assets/img/prevencion/prev7.png';

const monitoreoCarouselImages: string[] = [m1, m2, m3, m4, m5, m6, m7];
const planificacionCarouselImages: string[] = [plan1, plan2, plan3, plan4, plan5];
const prevencionCarouselImages: string[] = [prev1, prev2, prev3, prev4, prev5, prev6, prev7];

interface EquiposEnAccionProps {
  selectedBenefit: number;
  activeRole: string;
  benefits: string[];
  onDownloadClick?: () => void;
}

const EquiposEnAccion: React.FC<EquiposEnAccionProps> = ({ selectedBenefit, activeRole, benefits, onDownloadClick }) => {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const isVistaPreviaMonitoreo = activeRole === 'jugador' && selectedBenefit === 2;
  const isVistaPreviaPrevencion = activeRole === 'gestiono-equipo' && selectedBenefit === 2;
  const isVistaPreviaPlanificacion = activeRole === 'seguidor' && selectedBenefit === 2;
  const vistaPreviaImages = isVistaPreviaPrevencion
    ? prevencionCarouselImages
    : isVistaPreviaPlanificacion
      ? planificacionCarouselImages
      : monitoreoCarouselImages;
  const isVistaPrevia = isVistaPreviaMonitoreo || isVistaPreviaPrevencion || isVistaPreviaPlanificacion;

  useEffect(() => {
    if (isVistaPrevia) setCarouselIndex(0);
  }, [activeRole, selectedBenefit, isVistaPrevia]);

  useEffect(() => {
    if (!isVistaPrevia || vistaPreviaImages.length === 0) return;
    const id = setInterval(() => {
      setCarouselIndex((i) => (i + 1) % vistaPreviaImages.length);
    }, 2000);
    return () => clearInterval(id);
  }, [isVistaPrevia, vistaPreviaImages.length]);

  // Título dinámico basado en el rol
  const getSectionTitle = (role: string) => {
    switch (role) {
      case 'gestiono-equipo':
        return 'EQUIPOS EN ACCIÓN';
      case 'jugador':
        return 'JUGADORES EN ACCIÓN';
      case 'seguidor':
        return 'SEGUIDORES EN ACCIÓN';
      default:
        return 'EN ACCIÓN';
    }
  };

  // Contenido dinámico basado en el rol y beneficio seleccionado
  const getContentForBenefit = (role: string, benefitIndex: number) => {
    if (role === 'gestiono-equipo') {
      const benefitTitles = [
        "FUNCIONALIDAD",
        "IMPORTANCIA", 
        "VISTA PREVIA"
      ];

      const benefitDescriptions = [
        (
          <p>
            Herramientas de <strong>evaluación y monitoreo</strong> para la <strong>prevención de lesiones</strong> en deportistas.
          </p>
        ),
        (
          <>
            <p>
              Con el uso de la app, la <strong>comunicación</strong> entre entrenador y atleta es efectiva y directa.
            </p>
            <p>&nbsp;</p>
            <p>
              Esto permite que el entrenador esté siempre al tanto de cualquier molestia que los atletas puedan experimentar durante los entrenamientos.
            </p>
            <p>&nbsp;</p>
            <p>
              De esta manera, puede tomar medidas preventivas y ajustar el plan de entrenamiento para <strong>evitar lesiones futuras</strong>.
            </p>
          </>
        ),
        "Potenciá el crecimiento de tu equipo con herramientas de gestión profesional. Organizá agendas y convocatorias, administrá las finanzas y optimizá el entrenamiento y nutrición de tus jugadores."
      ];

      const benefitFeatures = [
        [
          { icon: Activity, text: "Herramienta de monitoreo de déficit de ROM (Rango de Movimiento)." },
          { icon: TrendingUp, text: "Medición de fuerza analítica." },
          { icon: Heart, text: "Monitoreo de estabilidad (Y balance test)." },
          { icon: Activity, text: "Monitoreo de potencia (Hops test, CMJ, DJ)." }
        ],
        [],
        [
          { icon: Calendar, text: "Gestión agenda y convocatoria" },
          { icon: DollarSign, text: "Finanzas" },
          { icon: Activity, text: "Entrenamiento y Nutrición" }
        ]
      ];

      const buttonTexts = [
        "Crear Identidad",
        "Conectar Ahora", 
        "Comenzar a Crecer"
      ];

      return {
        title: benefitTitles[benefitIndex] || benefitTitles[0],
        description: benefitDescriptions[benefitIndex] || benefitDescriptions[0],
        features: benefitFeatures[benefitIndex] || benefitFeatures[0],
        buttonText: buttonTexts[benefitIndex] || buttonTexts[0]
      };
    }

    if (role === 'jugador') {
      const benefitTitles = [
        "FUNCIONALIDAD",
        "IMPORTANCIA", 
        "VISTA PREVIA"
      ];

      const benefitDescriptions = [
        (
          <p>
            Herramientas profesionales para <strong>monitorear y evaluar</strong> la <strong>carga de entrenamiento</strong> de tus deportistas mediante diferentes <strong>métodos validados científicamente</strong>.
          </p>
        ),
        (
          <>
            <p>
              El <strong>monitoreo de la carga interna</strong> es esencial para evaluar el impacto del entrenamiento en el cuerpo y reducir el <strong>riesgo de lesiones</strong>.
            </p>
            <p>&nbsp;</p>
            <p>
              Nuestra herramienta les brinda a los entrenadores un <strong>control total sobre la carga de entrenamiento</strong> de los atletas, para ayudar a evitar el <strong>sobreentrenamiento y la fatiga</strong>.
            </p>
          </>
        ),
        "Potenciá tu desarrollo como futbolista con entrenamiento personalizado adaptado a tus condiciones y objetivos específicos. Accedé a planes nutricionales diseñados a medida para optimizar tu rendimiento y participá en capacitaciones especializadas en fútbol para perfeccionar tu técnica y táctica."
      ];

      const benefitFeatures = [
        [
          { icon: FileText, text: <><strong>Cuestionarios Wellness</strong> en las sesiones de entrenamiento.</> },
          { icon: Activity, text: <>Test de Calidad de Recuperación Total (<strong>TQR</strong>).</> },
          { icon: TrendingUp, text: <>Test de Seguimiento de Escala de Esfuerzo Percibido (<strong>RPE</strong>).</> },
          { icon: FileCheck, text: <>Cálculo de <strong>Unidades Arbitrarias</strong>.</> }
        ],
        [
          { icon: TrendingUp, text: "Carga Planificada vs. Carga Diaria Percibida" },
          { icon: Activity, text: "Sumatoria de UA" },
          { icon: FileText, text: "Variación de % de UA" },
          { icon: FileCheck, text: "Índice de Fatiga Aguda vs. Índice de Monotonía" },
          { icon: Heart, text: "Índice de Fatiga Crónica vs. Índice de Adaptación" }
        ],
        [
          { icon: Activity, text: "Entrenamiento personalizado" },
          { icon: Calendar, text: "Plan nutricional a medida" },
          { icon: FileText, text: "Capacitaciones en fútbol" }
        ]
      ];

      const buttonTexts = [
        "Crear Identidad",
        "Conectar Ahora", 
        "Comenzar a Crecer"
      ];

      return {
        title: benefitTitles[benefitIndex] || benefitTitles[0],
        description: benefitDescriptions[benefitIndex] || benefitDescriptions[0],
        features: benefitFeatures[benefitIndex] || benefitFeatures[0],
        buttonText: buttonTexts[benefitIndex] || buttonTexts[0]
      };
    }

    if (role === 'seguidor') {
      const benefitTitles = [
        "FUNCIONALIDAD",
        "IMPORTANCIA", 
        "VISTA PREVIA"
      ];

      const benefitDescriptions = [
        (
          <p>
            <strong>Biblioteca de ejercicios</strong> y tecnología para planificar, asignar y dar <strong>seguimiento</strong> a los planes de entrenamiento de los atletas.
          </p>
        ),
        (
          <>
            <p>
              Los entrenadores pueden <strong>optimizar el entrenamiento</strong> de los atletas y <strong>maximizar sus resultados</strong>.
            </p>
            <p>&nbsp;</p>
            <p>
              Esto fomenta el desarrollo de las principales <strong>capacidades físicas</strong>, la fuerza en distintas manifestaciones, la velocidad, la estabilidad, la flexibilidad y la resistencia.
            </p>
            <p>&nbsp;</p>
            <p>
              Gracias a la app, los entrenadores pueden <strong>brindar la orientación y el apoyo</strong> que los atletas necesitan para alcanzar sus <strong>objetivos</strong> de forma simple y rápida.
            </p>
            <p>&nbsp;</p>
            <p>
              <strong>No más pérdida de tiempo y energía</strong> en entrenamientos genéricos.
            </p>
            <p>&nbsp;</p>
            <p>
              Ayudá a tus atletas a <strong>alcanzar sus objetivos</strong> con la sección de planes de entrenamiento personalizados y la biblioteca audiovisual de ejercicios.
            </p>
          </>
        ),
        "Crece como hincha con beneficios exclusivos. Convertite en socio Coquita, participá en colectas y comprá en el vestuario de fútbol con descuentos especiales."
      ];

      const benefitFeatures = [
        [
          { 
            icon: Activity, 
            text: (
              <>
                <strong>Más de 1000 VIDEOS</strong> de ejercicios para asignar a los atletas, organizados estratégicamente.
                {"\n"}Ejercicios de:
                <ul className="list-disc list-outside mt-2 pl-4">
                  <li>Fuerza.</li>
                  <li>Estabilidad de zona media (core), estática y dinámica de MMII.</li>
                  <li>Flexibilidad estática y dinámica, liberación miofascial y movilidad articular.</li>
                  <li>Velocidad, cambios de dirección y variedad de skipping para mejorar la técnica de carrera.</li>
                </ul>
              </>
            )
          },
          { 
            icon: FileText, 
            text: (
              <>
                <strong>Soporte</strong> audiovisual y escrito para cada ejercicio.
              </>
            )
          },
          { 
            icon: Calendar, 
            text: (
              <>
                Tecnología para la <strong>creación de planes de entrenamiento</strong> fácil y rápido.
              </>
            )
          },
          { 
            icon: Users, 
            text: (
              <>
                Ayuda para el <strong>seguimiento y apoyo</strong> constante de los entrenadores a los atletas.
              </>
            )
          }
        ],
        [],
        [
          { icon: Gift, text: "Socios coquita" },
          { icon: HandCoins, text: "Apoyo en colectas" },
          { icon: ShoppingCart, text: "Comprar en Vestuario de Futbol" }
        ]
      ];

      const buttonTexts = [
        "Crear Identidad",
        "Conectar Ahora", 
        "Comenzar a Crecer"
      ];

      return {
        title: benefitTitles[benefitIndex] || benefitTitles[0],
        description: benefitDescriptions[benefitIndex] || benefitDescriptions[0],
        features: benefitFeatures[benefitIndex] || benefitFeatures[0],
        buttonText: buttonTexts[benefitIndex] || buttonTexts[0]
      };
    }

    // Contenido por defecto para otros roles
    return {
      title: "Funcionalidad en desarrollo",
      description: "Esta funcionalidad estará disponible próximamente.",
      features: [
        { icon: Users, text: "Próximamente" },
        { icon: MessageCircle, text: "En desarrollo" },
        { icon: Bell, text: "Muy pronto" }
      ],
      buttonText: "Próximamente"
    };
  };

  const currentContent = getContentForBenefit(activeRole, selectedBenefit);

  return (
    <div className="bg-white rounded-3xl shadow-xl p-8 h-full flex flex-col">
      {/* Content - Flex column to ensure proper alignment */}
      <div className="flex-1 flex flex-col">
        {/* Title - Show section title only for non-equipo roles, otherwise show benefit title as main title */}
        <div className="mb-4">
          {activeRole === 'gestiono-equipo' || activeRole === 'jugador' || activeRole === 'seguidor' ? (
            <motion.h3 
              key={`${activeRole}-${selectedBenefit}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-3xl font-display font-bold text-mas10-dark text-center"
            >
              {currentContent.title}
            </motion.h3>
          ) : (
            <>
              <motion.h3 
                key={activeRole}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-2xl font-display font-bold text-mas10-dark mb-4"
              >
                {getSectionTitle(activeRole)}
              </motion.h3>
              <motion.h4 
                key={`${activeRole}-${selectedBenefit}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-2xl font-bold text-mas10-dark"
              >
                {currentContent.title}
              </motion.h4>
            </>
          )}
        </div>

        {/* Vista previa: carrusel para Monitoreo o Prevención de lesiones */}
        {isVistaPrevia && vistaPreviaImages.length > 0 ? (
          <div className="flex-1 flex flex-col items-center">
            <div className="w-full max-w-sm aspect-[9/16] overflow-hidden rounded-2xl bg-gray-100 flex items-center justify-center">
              <AnimatePresence mode="wait" initial={false}>
                <motion.img
                  key={carouselIndex}
                  src={vistaPreviaImages[carouselIndex]}
                  alt={`Vista previa ${carouselIndex + 1}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-full h-full object-contain"
                />
              </AnimatePresence>
            </div>
            <div className="flex justify-center gap-1.5 mt-4">
              {vistaPreviaImages.map((_: string, i: number) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Ir a imagen ${i + 1}`}
                  onClick={() => setCarouselIndex(i)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i === carouselIndex ? 'bg-mas10-primary scale-125' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Description */}
            <div className="mb-8">
              <motion.div 
                key={`desc-${activeRole}-${selectedBenefit}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-gray-600 leading-relaxed whitespace-pre-line"
              >
                {currentContent.description}
              </motion.div>
            </div>
            
            {/* Features */}
            <div className="mb-8">
              <div className="space-y-3">
                {currentContent.features.map((feature, index) => (
                  <motion.div
                    key={`${activeRole}-${selectedBenefit}-${index}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
                    className="flex items-center space-x-3 w-full"
                  >
                    <div className="w-8 h-8 bg-mas10-accent rounded-full flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-4 h-4 text-mas10-dark" />
                    </div>
                    <span className="text-gray-700 flex-1 whitespace-pre-line">{feature.text}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EquiposEnAccion;
